import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { CalendarEvent } from "@cohesive/api-entities";
import { capitalCase } from "change-case";
import { format, isSameDay } from "date-fns";
import { Calendar, ExternalLink, PlusSquare, Users } from "lucide-react";
import { useMemo } from "react";
import { IntegrationIcon } from "../../IntegrationIcon";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export type { CalendarEvent };

type CalendarEventCardProps = CalendarEvent & BaseEntityCardProps;

// Avatar component for CalendarEventCard
export function CalendarEventCardAvatar({
  className,
  style,
}: Partial<CalendarEventCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <Calendar className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function CalendarEventCard({
  platform = "google-calendar",
  summary = "Calendar Event",
  status,
  attendees = [],
  html_link,
  start_date_time,
  end_date_time,
  start_time_zone,
  location,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: CalendarEventCardProps) {
  const formatTimeRange = (startStr?: string, endStr?: string) => {
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    try {
      if (startStr) startDate = new Date(startStr);
    } catch {
      startDate = null;
    }
    try {
      if (endStr) endDate = new Date(endStr);
    } catch {
      endDate = null;
    }

    const formatDateTime = (date: Date, fmt: string) => format(date, fmt);

    if (startDate && endDate) {
      const fullFormat = "MMM d, h:mm a";
      const timeOnlyFormat = "h:mm a";
      const formattedStart = formatDateTime(startDate, fullFormat);

      if (isSameDay(startDate, endDate)) {
        const formattedEnd = formatDateTime(endDate, timeOnlyFormat);
        return `${formattedStart} - ${formattedEnd}`;
      } else {
        const formattedEnd = formatDateTime(endDate, fullFormat);
        return `${formattedStart} - ${formattedEnd}`;
      }
    } else if (startDate) {
      return formatDateTime(startDate, "MMM d, h:mm a");
    } else if (endDate) {
      return formatDateTime(endDate, "MMM d, h:mm a");
    } else {
      return "";
    }
  };

  const timeRange = formatTimeRange(start_date_time, end_date_time);

  const timezoneAbbr = useMemo(() => {
    if (!start_time_zone) return "";

    try {
      const date = new Date();
      const formatter = new Intl.DateTimeFormat("en", {
        timeZone: start_time_zone,
        timeZoneName: "short",
      });
      const formatted = formatter.format(date);
      const parts = formatted.split(" ");
      const abbr = parts[parts.length - 1];
      return abbr || start_time_zone;
    } catch {
      return start_time_zone;
    }
  }, [start_time_zone]);

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "confirmed":
        return "bg-spectrum-green text-white border-green-600";
      case "tentative":
        return "bg-yellow-500 text-white border-yellow-500";
      case "cancelled":
        return "bg-red-600 text-white border-red-600";
      default:
        return "bg-neutral-500 text-white border-neutral-500";
    }
  };

  const attendeeCount = attendees.length;
  const maxAttendeesToShow = 10;
  const displayedAttendees = attendees.slice(0, maxAttendeesToShow);
  const remainingAttendeeCount =
    attendeeCount > maxAttendeesToShow ? attendeeCount - maxAttendeesToShow : 0;

  const isLocationUrl = useMemo(() => {
    if (!location) return false;
    try {
      new URL(location);
      return location.startsWith("http");
    } catch {
      return false;
    }
  }, [location]);

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <CalendarEventCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle
            platformIcon={
              platform && (
                <IntegrationIcon
                  platform={platform}
                  className="inline h-5 w-5 mr-2 align-middle"
                />
              )
            }
          >
            {summary}
          </BaseEntityCardTitle>
          <BaseEntityCardActions>
            {isLocationUrl && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={location}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className={cn(
                        buttonVariants({ variant: "ghost", size: "icon" }),
                        "h-6 w-6",
                      )}
                    >
                      <PlusSquare className="h-4 w-4" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Join meeting</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {html_link && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <a
                      href={html_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className={cn(
                        buttonVariants({ variant: "ghost", size: "icon" }),
                        "h-6 w-6",
                      )}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Open event</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {timeRange && (
            <BaseEntityCardMetadata>
              <BaseEntityCardMetadataItem
                icon={<Calendar className="h-3.5 w-3.5" />}
              >
                {timeRange}
                {timezoneAbbr && (
                  <span className="ml-1 text-xs opacity-70">
                    ({timezoneAbbr})
                  </span>
                )}
              </BaseEntityCardMetadataItem>
            </BaseEntityCardMetadata>
          )}

          <BaseEntityCardTags>
            {attendeeCount > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground cursor-default">
                      <Users className="h-3 w-3" />
                      <span>
                        {attendeeCount} attendee
                        {attendeeCount > 1 ? "s" : ""}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-0.5 text-xs">
                      {displayedAttendees.map((attendee, index) => (
                        <div key={index}>{attendee.email}</div>
                      ))}
                      {remainingAttendeeCount > 0 && (
                        <div className="mt-1 text-muted-foreground">
                          +{remainingAttendeeCount} more
                        </div>
                      )}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {status && (
              <Badge
                variant="outline"
                className={cn("text-xs", getStatusColor(status))}
              >
                {capitalCase(status)}
              </Badge>
            )}
          </BaseEntityCardTags>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function CalendarEventCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={cn("h-[90px]", className)} style={style}>
      <Skeleton className="h-10 w-10 rounded-full" />
      <BaseEntityCardContent>
        <Skeleton className="h-4 w-2/3 mt-1.5 mb-1.5" />
        <Skeleton className="h-3 w-1/2 mb-1.5" />
        <Skeleton className="h-3 w-3/4" />
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
